// Which tmux_session_lost deaths are routine recycles (nothing was interrupted) rather than a task dying mid-flight.
// Split out of layout.tsx so the rules have one home and a unit test.

// Which `death_class_signal_event` values (tmux_pruner.py's
// `_INTENTIONAL_TEARDOWN_SIGNAL_EVENTS`) are GENUINELY zero-cost — no task was
// interrupted, nothing needs re-doing — vs. the server merely RECOGNIZING what
// killed the session, which is a different claim entirely (2026-08-24, operator
// correction: the first cut of this treated `death_class==="intentional_teardown"`
// as "nothing to see here", conflating both). Checked each site's actual
// behavior in server/, not guessed from the name:
//   - worker_one_task_per_session_reset: fires AFTER a clean /done — the task
//     already finished; this just recycles the session (server/*.py).
//   - orphan_session_reclaim / idle_lingering_session_reclaim
//     (worker_liveness_watchdog.py): the reclaimed session had NO live task —
//     "idle_lingering" is explicit about it; "orphan" means ownerless.
//   - usage_cap_resume_initiated / heartbeat_silent_resume_initiated
//     (worker_liveness_watchdog.py): logged on the SUCCESS path of a
//     kill-then-`--resume` — the transcript/context is preserved, unlike a
//     fresh respawn, so no progress is actually lost.
// Deliberately EXCLUDED (a task WAS interrupted and had to restart from
// scratch, even though the orchestrator's own recovery is automatic and safe):
//   - context_wedge_recovered (context_lifecycle.py `_recover_wedged_target`):
//     /compact repeatedly failed to shrink a saturated session; kill CLEARS
//     the resume pointer on purpose ("no resume target -> fresh respawn") —
//     whatever the worker was doing is gone, not continued.
//   - watchdog_slot_killed (worker_liveness_watchdog.py `_kill_slot`): the
//     SHARED teardown point for context_full/stuck_at_prompt/usage_cap/
//     nudge-cap-exhausted/heartbeat-silent-give-up — every one of those is a
//     session that got stuck and had to be forced out.
//   - blocked_slot_timed_out: a task sat unanswered past its timeout.
//   - heartbeat_resume_respawn_failed: an attempted --resume itself FAILED
//     (frozen account) — the failure path, not the success one above.
//   - worker_account_unusable_killed: a live worker killed mid-task because
//     its account fell out of dispatch headroom.
export const BENIGN_RECYCLE_SIGNAL_EVENTS = new Set([
  "worker_one_task_per_session_reset",
  "orphan_session_reclaim",
  "idle_lingering_session_reclaim",
  "usage_cap_resume_initiated",
  "heartbeat_silent_resume_initiated",
]);

// The recorded `kill_reason` of a death that is a clean post-completion recycle, or an operator action. Measured
// 2026-10-03 over 24h on the live AO DB: of ~326 tmux_session_lost rows, 89 were `one_shot_done_synchronous_free`
// (a one-shot cicd/plan_reconciler/etc. agent finished its work and its session was freed), 12
// `one_task_per_session_reset`, 10 `worker_compact_gated_one_task_reset`, 10 `node_proxy_done_reset_before_next`
// (all four fire after a /done) and 7 `api_pause_slot` (the operator's own pause). None carries a
// `death_class_signal_event`, so the signal-event set above never matched them and the Fleet Last Done column showed a red
// "died mid-task" for completed work. Only 2 of the 326 rows had a released (requeued) task.
export const BENIGN_RECYCLE_KILL_REASONS: ReadonlySet<string> = new Set([
  "one_shot_done_synchronous_free",
  "one_task_per_session_reset",
  "worker_compact_gated_one_task_reset",
  "node_proxy_done_reset_before_next",
  "api_pause_slot",
]);

// The subset of benign recycles that fire AFTER a task's `/done` and tear the session down so the NEXT task starts
// with no carried context: `one_shot_done_synchronous_free` is gated on a genuine fresh one-off completion
// (routes/slots_worker.py `done_slot`), the other three on the one-task-per-session reset
// (`_one_task_per_session_reset_response` and its node-proxy half). Excludes `api_pause_slot` (an operator pause
// completes nothing) and the signal-event recycles above (orphan/idle reclaim and resume-initiated kills carry no
// completion claim either).
const COMPLETION_RECYCLE_KILL_REASONS: ReadonlySet<string> = new Set([
  "one_shot_done_synchronous_free",
  "one_task_per_session_reset",
  "worker_compact_gated_one_task_reset",
  "node_proxy_done_reset_before_next",
]);

/** True when this death is the post-completion teardown of a task that FINISHED (the next task gets a fresh context),
 * not merely any benign recycle. Such a row is a "done" with a context-reset disposition, never a death. */
export function isCompletionRecycle(details: Record<string, unknown> | null | undefined): boolean {
  if (!details) return false;
  if (typeof details.kill_reason === "string" && COMPLETION_RECYCLE_KILL_REASONS.has(details.kill_reason)) return true;
  return (
    details.death_class === "intentional_teardown" &&
    details.death_class_signal_event === "worker_one_task_per_session_reset"
  );
}

/** True when this death is a routine recycle: an intentional teardown from a benign signal event, or a recorded benign kill reason. */
export function isBenignRecycle(details: Record<string, unknown> | null | undefined): boolean {
  if (!details) return false;
  if (typeof details.kill_reason === "string" && BENIGN_RECYCLE_KILL_REASONS.has(details.kill_reason)) return true;
  return (
    details.death_class === "intentional_teardown" &&
    typeof details.death_class_signal_event === "string" &&
    BENIGN_RECYCLE_SIGNAL_EVENTS.has(details.death_class_signal_event)
  );
}
